import { useEffect } from "react";

const ensureMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs.create || {}).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  Object.entries(attrs.set || {}).forEach(([k, v]) => el.setAttribute(k, v));
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id, data) => {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-jsonld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

/**
 * useSEO — sets title, meta description, Open Graph, Twitter cards, canonical, JSON-LD
 * @param {object} opts - { title, description, image, type, jsonLd }
 */
const useSEO = ({ title, description, image, type = "website", jsonLd } = {}) => {
  useEffect(() => {
    // Ensure the root <html> tag declares French locale (SEO)
    document.documentElement.setAttribute("lang", "fr");

    const base = "Passion Verre — Gravure & découpe laser sur mesure";
    const fullTitle = title ? `${title} · ${base}` : base;
    document.title = fullTitle;

    // Meta description ≤160 chars — Google truncates beyond that
    const desc = description || "Atelier artisanal de gravure et découpe laser sur mesure (Isère). Verre, bois, métal, acrylique. Mariage, cadeaux, entreprises. Livraison France.";

    // Build an absolute og:image URL rooted on the current domain (avoids depending on 3rd-party assets)
    const toAbsolute = (u) => {
      if (!u) return null;
      if (/^https?:\/\//i.test(u)) return u;
      if (u.startsWith("/")) return window.location.origin + u;
      return u;
    };
    const ogImage = toAbsolute(image) || (window.location.origin + "/og-image.jpg");

    ensureMeta('meta[name="description"]', { create: { name: "description" }, set: { content: desc } });
    ensureMeta('meta[property="og:title"]', { create: { property: "og:title" }, set: { content: fullTitle } });
    ensureMeta('meta[property="og:description"]', { create: { property: "og:description" }, set: { content: desc } });
    ensureMeta('meta[property="og:image"]', { create: { property: "og:image" }, set: { content: ogImage } });
    ensureMeta('meta[property="og:type"]', { create: { property: "og:type" }, set: { content: type } });
    ensureMeta('meta[property="og:url"]', { create: { property: "og:url" }, set: { content: window.location.href } });
    ensureMeta('meta[property="og:locale"]', { create: { property: "og:locale" }, set: { content: "fr_FR" } });
    ensureMeta('meta[property="og:site_name"]', { create: { property: "og:site_name" }, set: { content: "Passion Verre" } });
    ensureMeta('meta[name="twitter:card"]', { create: { name: "twitter:card" }, set: { content: "summary_large_image" } });
    ensureMeta('meta[name="twitter:title"]', { create: { name: "twitter:title" }, set: { content: fullTitle } });
    ensureMeta('meta[name="twitter:description"]', { create: { name: "twitter:description" }, set: { content: desc } });
    ensureMeta('meta[name="twitter:image"]', { create: { name: "twitter:image" }, set: { content: ogImage } });

    setLink("canonical", window.location.href.split("?")[0].split("#")[0]);

    if (jsonLd) setJsonLd("page", jsonLd);
  }, [title, description, image, type, JSON.stringify(jsonLd)]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useSEO;
